import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { personImageUrl } from '../../api/client';
import type { ItemPerson } from '../../api/types';
import PersonDialog from './PersonDialog';

/// The cast, as a strip that scrolls sideways. Somebody a provider gave an id to can be opened;
/// a bare name is shown and nothing more, because there is nothing more to show.
export default function CastRow({ people }: { people: ItemPerson[] }) {
  const { t, i18n } = useTranslation();
  const [opened, setOpened] = useState<ItemPerson>();

  const cast = people.filter((person) => person.kind === 'Actor' || person.kind === 'GuestStar');
  const crew = people.filter((person) => person.kind !== 'Actor' && person.kind !== 'GuestStar');

  if (people.length === 0) {
    return null;
  }

  return (
    <Stack spacing={1}>
      <Typography variant="h3">{t('item.cast')}</Typography>

      {cast.length > 0 && (
        <Box
          sx={{
            display: 'grid',
            gridAutoFlow: 'column',
            gridAutoColumns: '120px',
            gap: 2,
            overflowX: 'auto',

            // Room above for the portraits to grow into when a pointer is over them: a strip
            // that scrolls sideways clips every edge, not only the one it scrolls along. Part
            // of the gap below the heading is set here rather than by the stack above, so the
            // room costs the layout nothing.
            pt: 1,
            pb: 1,
          }}
        >
          {cast.map((person) => {
            const openable = Boolean(person.providerKey && person.personId);

            return (
              <Box
                key={`${person.name}-${person.role ?? ''}`}
                component={openable ? 'button' : 'div'}
                onClick={openable ? () => setOpened(person) : undefined}
                sx={{
                  appearance: 'none',
                  border: 0,
                  background: 'none',
                  padding: 0,
                  color: 'inherit',
                  textAlign: 'center',
                  cursor: openable ? 'pointer' : 'default',
                  display: 'grid',
                  justifyItems: 'center',
                  gap: 1,
                  '&:hover .portrait, &:focus-visible .portrait': openable
                    ? { transform: 'scale(1.04)' }
                    : undefined,
                }}
              >
                <Avatar
                  className="portrait"
                  src={
                    person.providerKey && person.personId
                      ? personImageUrl(person.providerKey, person.personId, i18n.language)
                      : undefined
                  }
                  alt=""
                  sx={{
                    width: 96,
                    height: 96,
                    fontSize: 32,
                    transition: 'transform 160ms ease',
                  }}
                >
                  {person.name.slice(0, 1)}
                </Avatar>

                <Box sx={{ minWidth: 0, width: '100%' }}>
                  <Typography variant="subtitle2" noWrap>
                    {person.name}
                  </Typography>
                  {person.role && (
                    <Typography variant="caption" color="text.secondary" noWrap sx={{ display: 'block' }}>
                      {person.role}
                    </Typography>
                  )}
                </Box>
              </Box>
            );
          })}
        </Box>
      )}

      {crew.length > 0 && (
        <Typography variant="body2" color="text.secondary">
          {crew.map((person) => `${t(`personKind.${person.kind}`)}: ${person.name}`).join(' · ')}
        </Typography>
      )}

      {opened && <PersonDialog person={opened} onClose={() => setOpened(undefined)} />}
    </Stack>
  );
}
